import React, { useState, useEffect, Suspense, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// import SubTitle from "../common/SubTitle";
// import Cards from "../common/Cards";
// import SearchFilter from "../common/SearchFilter";
import "./Vehicle.css";
// import Pagination from "../common/Pagination";
import axios from "axios";
import config from "../../config.js";
import activeEmoji from "../../images/vehicle/Active_icon.svg";
import inActiveEmoji from "../../images/vehicle/Inactive_icon.svg";
// import deadEmoji from "../../images/vehicle/Dead_icon.svg";
import DeleteModal from "../common/DeleteModal";
// import DynamicTable from "../common/DynamicTable";
import DynamicTable, {
  TableLastUpdate,
  TableStatusBadge,
} from "../common/New/DynamicTable";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { useSettings } from "../../context/SettingsContext";
import { reportsApi, getOrganizationId } from "../manageData/reportsApi.js";
import { getDeviceMonitorLatest } from "../device/deviceApi.js";
import {
  buildVehicleMonitorRows,
  normalizeSensorType,
} from "../../utils/vehicleMonitorData.js";
import { resolveVehicleLocationNames, buildGeocodeCacheKey } from "../../utils/reverseGeocodeCache.js";
import {
  extractLiveTelemetry,
  SENSOR_UNITS,
  DEFAULT_SENSOR_RANGES,
} from "../../pages/vehicle/vehicleTrack/liveTelemetry.js";
import { FLEET_VEHICLE_ROW_ACTIONS, hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFilterWidgetsFromLayout,
  buildFleetRowActionsFromLayout,
  buildFleetStatCards,
  buildTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
} from "../../layout/moduleLayoutUtils";
import SubTitleNew from "../common/New/SubTitleNew";
import StatsCard from "../common/New/StatsCard.jsx";
import DashboardGraph, {
  CUSTOM_DATE_OPTION,
  DASHBOARD_DATE_OPTIONS,
} from "./DashboardGraph.jsx";
import VehicleAddMain from "./VehicleAddMain";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { useUser } from "../../pages/login/UserContextPersistent";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SpeedIcon from "@mui/icons-material/Speed";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Alert, Box } from "@mui/material";
import { downloadBackendExcel, exportTableToCSV } from "../../utils/exportTableData";

// Helper to check if user is RouteEye Admin
const isRouteEyeAdmin = () => {
  const userState = localStorage.getItem("userState");
  if (!userState) return false;
  try {
    const user = JSON.parse(userState).user;
    return user.organizationId === 5 && user.email === "sales@routeeye.io";
  } catch {
    return false;
  }
};

// Custom headers for RouteEye Admin (with organization, site, and routes columns)
const getAdminHeaders = () => [
  { key: "vehicleName", value: "Vehicles" },
  { key: "VehicleNumber", value: "Vehicle Number" },
  { key: "organizationName", value: "Organization" },
  { key: "siteID", value: "Site" },
  { key: "routes", value: "Routes" },
  { key: "status", value: "Status" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const getHeaders = (canViewDrivers, canViewSites) => {
  const baseHeaders = [
    { key: "vehicleName", value: "Vehicles" },
    { key: "VehicleNumber", value: "Vehicle Number" },
    { key: "routes", value: "Routes" },
  ];

  console.log("Adding site column - permission check:", canViewSites);
  if (canViewSites) {
    baseHeaders.push({ key: "siteID", value: "Sites" });
  }

  // Add drivers column only if user has permission
  if (canViewDrivers) {
    baseHeaders.push({ key: "drivers", value: "Drivers" });
  }

  // Add status and actions columns
  baseHeaders.push({ key: "status", value: "Status" });
  baseHeaders.push({ key: ACTIONS_COLUMN_ID, value: "Actions" });

  return baseHeaders;
};

const VEHICLE_TABLE_BATCH_SIZE = 10;
const SPEED_INCIDENT_TABLE_BATCH_SIZE = 10;
const DEFAULT_OVERSPEED_LIMIT = 60;
const SPEED_THRESHOLD_OFFSETS = [-5, 0, 5, 10, 20];
const LIVE_TELEMETRY_REFRESH_MS = 15000;

const fingerprintLiveSnapshot = (locations = {}, devices = {}) => {
  const locationPart = Object.keys(locations)
    .sort()
    .map((id) => {
      const location = locations[id] || {};
      const telemetry = location.telemetry || {};
      const telemetryPart = Object.keys(telemetry)
        .sort()
        .map((key) => `${key}:${telemetry[key]}`)
        .join(",");
      return `${id}:${location.latitude ?? ""},${location.longitude ?? ""},${location.speed ?? ""},${location.timestamp ?? ""},{${telemetryPart}}`;
    })
    .join("|");

  const devicePart = Object.keys(devices)
    .sort()
    .map((id) => {
      const list = devices[id] || [];
      return `${id}:${list
        .map((device) => `${device.id}:${device.val ?? ""}:${device.status ?? ""}`)
        .join(",")}`;
    })
    .join("|");

  return `${locationPart}::${devicePart}`;
};

const LIVE_SENSOR_LABELS = {
  fuel: "Fuel",
  fuel_level: "Fuel",
  temperature: "Temperature",
  humidity: "Humidity",
  pressure: "Pressure",
  tyre_pressure: "Pressure",
  vibration: "Vibration",
  smoke: "Smoke",
  load_weight: "Load",
  engine_health: "Engine",
  battery_voltage: "Battery",
  voltage: "Voltage",
  ignition: "Ignition",
};

// Metrics that can arrive embedded in GPS / location payloads.
const GPS_EMBEDDED_METRICS = [
  { key: "temperature", label: "Temperature" },
  { key: "humidity", label: "Humidity" },
  { key: "voltage", label: "Voltage" },
  { key: "vibration", label: "Vibration" },
  { key: "smoke", label: "Smoke" },
];

const pickNonNullTelemetry = (telemetry = {}) =>
  Object.fromEntries(
    Object.entries(telemetry).filter(([key, value]) => {
      if (
        key === "speed" ||
        key === "satellites" ||
        key === "ignition" ||
        key === "ranges" ||
        key === "sensors"
      ) {
        return false;
      }
      if (value === null || value === undefined || value === "") return false;
      const number = Number(value);
      // Location/GPS payloads often default missing sensors to 0 — don't treat that as real data.
      if (!Number.isFinite(number) || number === 0) return false;
      return true;
    }),
  );

const formatGpsEmbeddedValue = (key, value) => {
  const unit = SENSOR_UNITS[key] || "";
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const display =
    Number.isInteger(number) || Math.abs(number) >= 100
      ? Math.round(number)
      : number.toFixed(1);
  return `${display}${unit}`;
};

const getGpsEmbeddedTone = (key, value) => {
  const range = DEFAULT_SENSOR_RANGES[key];
  const number = Number(value);
  if (!range || !Number.isFinite(number)) return "green";
  if (number < range.min || number > range.max) return "red";
  const span = range.max - range.min;
  if (span > 0) {
    const warning = span * 0.1;
    if (number - range.min <= warning || range.max - number <= warning) {
      return "amber";
    }
  }
  return "green";
};

const formatCoordinate = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num !== 0 ? num.toFixed(6) : null;
};

const isGpsDevice = (device) => {
  const type = normalizeSensorType(device?.type || device?.sensorType || device?.deviceType);
  return type.includes("gps") || type.includes("tracker");
};

const isLiveSensorDevice = (device) =>
  device?.val !== undefined &&
  device?.min !== undefined &&
  device?.max !== undefined &&
  !isGpsDevice(device);

const getSensorTone = (device) => {
  if (!isLiveSensorDevice(device)) return "green";
  const type = normalizeSensorType(device.type);
  const isFuel = type === "fuel" || type === "fuel_level";
  if (isFuel ? device.val < device.min : device.val < device.min || device.val > device.max) {
    return "red";
  }
  const range = device.max - device.min;
  if (Number.isFinite(range) && range > 0) {
    const warningDistance = range * 0.1;
    const nearLow = device.val - device.min <= warningDistance;
    const nearHigh = device.max - device.val <= warningDistance;
    if (isFuel ? nearLow : nearLow || nearHigh) return "amber";
  }
  return "green";
};

const formatSensorValue = (device) => {
  if (normalizeSensorType(device.type) === "ignition") {
    return device.val >= 0.5 ? "ON" : "OFF";
  }
  const value =
    Number.isInteger(device.val) || Math.abs(device.val) >= 100
      ? Math.round(device.val)
      : Number(device.val).toFixed(1);
  return `${value}${device.unit || ""}`;
};

const formatSensorDelta = (device, previousValue) => {
  if (previousValue === undefined || previousValue === null) return null;
  const delta = Number(device.val) - Number(previousValue);
  if (!Number.isFinite(delta) || delta === 0) return null;
  const rounded =
    Math.abs(delta) >= 10 ? Math.round(delta) : Number(delta.toFixed(1));
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
};

// Inactive/DEAD vehicles are not moving — never show a stale GPS speed.
const getDisplayedVehicleSpeed = (speed, status) => {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "DEAD" || normalized === "INACTIVE") return 0;
  const speedValue = Number.parseFloat(speed);
  return Number.isFinite(speedValue) ? speedValue : 0;
};

const buildLiveDataCards = ({
  vehicle,
  devices = [],
  liveLocation,
  previousSensorValues = {},
}) => {
  const cards = [];
  const latitude = formatCoordinate(
    liveLocation?.latitude ?? vehicle?.latitude,
  );
  const longitude = formatCoordinate(
    liveLocation?.longitude ?? vehicle?.longitude,
  );

  // Lat/lng means a GPS fix is available — show GPS type only (not the coords).
  const gpsDevice = devices.find(isGpsDevice);
  if (gpsDevice || (latitude && longitude)) {
    cards.push({
      id: gpsDevice?.id || `gps-${vehicle.id}`,
      label: "GPS",
      display: null,
      tone: "green",
      delta: null,
      typeOnly: true,
    });
  }

  const coveredTypes = new Set();

  devices.filter(isLiveSensorDevice).forEach((device) => {
    const type = normalizeSensorType(device.type);
    coveredTypes.add(type);
    if (type === "battery_voltage") coveredTypes.add("voltage");
    if (type === "voltage") coveredTypes.add("battery_voltage");

    const label =
      LIVE_SENSOR_LABELS[type] ||
      String(device.type || "Sensor").replace(/_/g, " ");
    cards.push({
      id: device.id,
      label,
      display: formatSensorValue(device),
      tone: getSensorTone(device),
      delta: formatSensorDelta(device, previousSensorValues[device.id]),
    });
  });

  // GPS payloads can also carry temp / humidity / voltage / etc.
  const gpsTelemetry = liveLocation?.telemetry || {};
  GPS_EMBEDDED_METRICS.forEach(({ key, label }) => {
    if (coveredTypes.has(key)) return;
    if (key === "voltage" && coveredTypes.has("battery_voltage")) return;

    const rawValue = gpsTelemetry[key];
    const numericValue = Number(rawValue);
    // Only show when a real non-zero reading is present in the payload.
    if (!Number.isFinite(numericValue) || numericValue === 0) return;

    const display = formatGpsEmbeddedValue(key, rawValue);
    if (!display) return;

    const previousValue = previousSensorValues[`gps-${key}`];
    const delta =
      previousValue === undefined || previousValue === null
        ? null
        : formatSensorDelta({ val: rawValue }, previousValue);

    cards.push({
      id: `gps-${vehicle.id}-${key}`,
      label,
      display,
      tone: getGpsEmbeddedTone(key, rawValue),
      delta,
    });
    coveredTypes.add(key);
  });

  return cards;
};

// The thresholds are anchored to the overspeed limit configured in app settings
// so the filter stays meaningful per organization.
const buildSpeedThresholdOptions = (overspeedLimit) => {
  const limit = Number(overspeedLimit) > 0 ? Number(overspeedLimit) : DEFAULT_OVERSPEED_LIMIT;

  const thresholds = [
    ...new Set(
      SPEED_THRESHOLD_OFFSETS.map((offset) => Math.round(limit + offset)).filter(
        (value) => value > 0,
      ),
    ),
  ].sort((a, b) => a - b);

  return [
    { value: "", label: "All Speeds" },
    ...thresholds.map((value) => ({
      value: String(value),
      label:
        value === Math.round(limit)
          ? `Above limit (${value} km/h)`
          : `Above ${value} km/h`,
    })),
  ];
};

const VEHICLE_PAGE_CACHE_MS = 60 * 1000;
const vehicleListCache = new Map();
const vehicleFleetCache = new Map();
const vehicleSpeedCache = new Map();
const vehicleOverspeedCountCache = new Map();

const SPEED_INCIDENT_DATE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last 7 days", label: "Last 7 Days" },
  { value: "last 14 days", label: "Last 14 Days" },
  { value: "last 30 days", label: "Last 30 Days" },
];

const getFirstValue = (row, keys, fallback = "") => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const getReportRows = (report) => {
  if (Array.isArray(report?.data)) return report.data;
  if (Array.isArray(report?.sections)) {
    return report.sections.flatMap((section) => (Array.isArray(section?.data) ? section.data : []));
  }
  return [];
};

const EMPTY_FLEET_METRICS = {
  distanceCovered: 0,
  totalRuntime: 0,
  movingHours: 0,
  tripsStarted: 0,
  completedTrips: 0,
  onTimePerformance: null,
  vehiclesRunning: 0,
  vehiclesNeedAttention: 0,
  vehiclesTotal: 0,
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getExactDayReportParams = (dateOption) => {
  const option = toReportDateOption(dateOption);
  if (option !== "today" && option !== "yesterday") return null;

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (option === "yesterday") start.setDate(start.getDate() - 1);

  const end = new Date(start);
  end.setHours(23, 59, 59, 999);

  return {
    dateOption: CUSTOM_DATE_OPTION,
    customStartDate: start.toISOString(),
    customEndDate: end.toISOString(),
  };
};

const buildFleetMetrics = (runtimeReport, tripReport) => {
  const runtimeRows = getReportRows(runtimeReport);
  return {
    distanceCovered: sumReportColumn(runtimeRows, "noOfKmsCovered"),
    totalRuntime: sumReportColumn(runtimeRows, "totalRuntimeHours"),
    movingHours: sumReportColumn(runtimeRows, "totalMovingHours"),
    ...summarizeTripStatuses(getReportRows(tripReport)),
    ...summarizeRuntimeActivity(runtimeRows),
  };
};

// Top-right card badges: % change vs the previous equivalent period.
// For the default "today" view that previous period is yesterday.
const getComparisonPeriodParams = (dateOption, customRange = {}) => {
  const option = toReportDateOption(dateOption);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (option === "today") {
    return { dateOption: "yesterday" };
  }

  if (option === "yesterday") {
    const day = new Date(today);
    day.setDate(today.getDate() - 2);
    return {
      dateOption: CUSTOM_DATE_OPTION,
      customStartDate: formatDateInput(day),
      customEndDate: formatDateInput(day),
    };
  }

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  let start = new Date(today);
  let end = endOfToday;

  if (option === CUSTOM_DATE_OPTION) {
    start = new Date(customRange.start);
    end = new Date(customRange.end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return { dateOption: "yesterday" };
    }
    end.setHours(23, 59, 59, 999);
  } else if (option === "last 7 days") {
    start.setDate(today.getDate() - 6);
  } else if (option === "last 14 days") {
    start.setDate(today.getDate() - 13);
  } else if (option === "last 30 days") {
    start.setDate(today.getDate() - 29);
  } else if (option === "this week") {
    const weekday = today.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    start.setDate(today.getDate() + mondayOffset);
  } else if (option === "this month") {
    start.setDate(1);
  } else {
    return { dateOption: "yesterday" };
  }

  const durationMs = Math.max(end.getTime() - start.getTime(), 24 * 60 * 60 * 1000 - 1);
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd.getTime() - durationMs);

  return {
    dateOption: CUSTOM_DATE_OPTION,
    customStartDate: formatDateInput(prevStart),
    customEndDate: formatDateInput(prevEnd),
  };
};

const computePercentChangeTrend = (current, previous) => {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }

  const currentValue = Number(current);
  const previousValue = Number(previous);
  if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue)) return null;

  if (previousValue === 0) {
    if (currentValue === 0) return null;
    return {
      trend: "100%",
      trendDirection: currentValue > 0 ? "up" : "down",
    };
  }

  const change = Math.round(((currentValue - previousValue) / Math.abs(previousValue)) * 100);
  if (change === 0) return null;

  return {
    trend: `${Math.abs(change)}%`,
    trendDirection: change > 0 ? "up" : "down",
  };
};

// On-time performance is already a percentage. Show the direct difference
// between today and yesterday (15% vs 2% = an increase of 13%).
const computePerformanceDifferenceTrend = (current, previous) => {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }

  const currentValue = Number(current);
  const previousValue = Number(previous);
  if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue)) return null;

  const change = Math.round((currentValue - previousValue) * 10) / 10;
  if (change === 0) return null;

  return {
    trend: `${Math.abs(change)}%`,
    trendDirection: change > 0 ? "up" : "down",
  };
};

const getNumericMetric = (vehicle, keys = []) => {
  const value = keys
    .map((key) => vehicle?.[key])
    .find((metric) => metric !== undefined && metric !== null && metric !== "");
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const summarizeRuntimeActivity = (rows) => {
  const vehiclesTotal = rows.length;
  const vehiclesRunning = rows.filter((row) => {
    const movingHours = Number(row?.totalMovingHours);
    const distance = Number(row?.noOfKmsCovered);
    const trips = Number(row?.noOfTrips);
    return (
      (Number.isFinite(movingHours) && movingHours > 0) ||
      (Number.isFinite(distance) && distance > 0) ||
      (Number.isFinite(trips) && trips > 0)
    );
  }).length;

  return {
    vehiclesRunning,
    vehiclesNeedAttention: Math.max(vehiclesTotal - vehiclesRunning, 0),
    vehiclesTotal,
  };
};

const dashboardRangeIncludesToday = (dateOption, customRange = {}) => {
  const option = toReportDateOption(dateOption);
  if (option === "yesterday") return false;
  if (option !== "custom") return true;
  if (!customRange.start || !customRange.end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(customRange.start);
  const end = new Date(customRange.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return start <= today && end >= today;
};

const toReportDateOption = (value) =>
  String(value || "today")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

const sumReportColumn = (rows, key) =>
  rows.reduce((total, row) => {
    const value = Number(row?.[key]);
    return Number.isFinite(value) ? total + value : total;
  }, 0);

// The trip report returns one row per trip, with the formatted status produced by
// determineTripStatus (In Progress / In-Time / Early / Delayed / Completed /
// Not Completed). Trips still running can't count for or against punctuality.
const summarizeTripStatuses = (rows) => {
  const completedTrips = rows.filter(
    (row) => String(row?.status || "").trim() === "Completed",
  ).length;
  const delayedTrips = rows.filter((row) => {
    const status = String(row?.status || "").trim();
    return status.includes("Not Completed") || status.includes("over");
  }).length;
  const scoredTrips = completedTrips + delayedTrips;

  return {
    tripsStarted: rows.length,
    completedTrips: scoredTrips,
    onTimePerformance: scoredTrips
      ? Math.round((completedTrips / scoredTrips) * 100)
      : null,
  };
};

const parseDurationMinutes = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const text = String(value).trim().toLowerCase();
  const hours = Number.parseFloat(text.match(/([\d.]+)\s*h/)?.[1] || 0);
  const minutes = Number.parseFloat(text.match(/([\d.]+)\s*m/)?.[1] || 0);
  const seconds = Number.parseFloat(text.match(/([\d.]+)\s*s/)?.[1] || 0);
  if (hours || minutes || seconds) return hours * 60 + minutes + seconds / 60;

  const numeric = Number.parseFloat(text);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatDurationMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return "N/A";
  if (minutes < 1) return `${Math.round(minutes * 60)} secs`;
  if (minutes < 60) return `${Math.round(minutes)} mins`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

const formatOccurredAt = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getRouteMatchValue = (row) =>
  String(
    getFirstValue(row, ["routeId", "route_id", "routeID", "routeName", "route", "Route"], ""),
  );

const mapOverspeedReportRows = (rows) => {
  const groups = new Map();

  rows.forEach((row, index) => {
    const vehicleName = getFirstValue(
      row,
      ["vehicleName", "Vehicle Name", "vehicle", "name", "VehicleName"],
      "N/A",
    );
    const vehicleNumber = getFirstValue(
      row,
      ["vehicleNumber", "Vehicle Numbers", "VehicleNumber", "regNo", "registrationNumber", "device_id"],
      "",
    );
    const route = getFirstValue(row, ["routeName", "route", "Route", "route_name"], "N/A");
    const driver = getFirstValue(row, ["driverName", "driver", "Driver", "driver_name"], "Unassigned");
    const driverPhone = getFirstValue(row, ["driverPhone", "driver_phone", "phone", "mobile"], "");
    const speed = Number.parseFloat(getFirstValue(row, ["maxSpeed", "speed", "Speed", "speedKmph"], 0)) || 0;
    const occurredAt = getFirstValue(row, ["timestamp", "time", "createdAt", "date"], "");
    const duration = parseDurationMinutes(
      getFirstValue(row, ["totalDuration", "duration", "overspeedDuration", "durationMinutes"], ""),
    );
    const groupKey = [
      getFirstValue(row, ["vehicleId", "device_id", "vehicle_id"], vehicleNumber || vehicleName),
      route,
      driver,
    ].join("|");

    const current = groups.get(groupKey) || {
      id: groupKey || `overspeed-${index}`,
      vehicleName,
      vehicleNumber,
      route,
      routeMatchValue: getRouteMatchValue(row),
      origin: getFirstValue(row, ["origin", "originName", "source", "startLocation", "from"], "N/A"),
      destination: getFirstValue(row, ["destination", "destinationName", "endLocation", "to"], "N/A"),
      driverName: driver || "Unassigned",
      driverPhone,
      maxSpeed: 0,
      overspeedCount: 0,
      totalDurationMinutes: null,
      lastOccurredRaw: "",
      lastOccurred: "N/A",
    };

    current.maxSpeed = Math.max(current.maxSpeed, speed);
    current.overspeedCount += 1;
    if (duration !== null) {
      current.totalDurationMinutes = (current.totalDurationMinutes || 0) + duration;
    }

    const currentTime = new Date(current.lastOccurredRaw).getTime();
    const nextTime = new Date(occurredAt).getTime();
    if (!current.lastOccurredRaw || (!Number.isNaN(nextTime) && (Number.isNaN(currentTime) || nextTime > currentTime))) {
      current.lastOccurredRaw = occurredAt;
      current.lastOccurred = formatOccurredAt(occurredAt);
    }

    groups.set(groupKey, current);
  });

  return Array.from(groups.values())
    .map((row) => ({
      ...row,
      maxSpeedLabel: row.maxSpeed ? Math.round(row.maxSpeed) : "N/A",
      totalDuration: formatDurationMinutes(row.totalDurationMinutes),
    }))
    .sort((a, b) => (Number(b.maxSpeed) || 0) - (Number(a.maxSpeed) || 0));
};

export default function VehicleMain() {
  const { t } = useTranslation();
  const { organizationInactive } = useUser();
  const [vehicleData, setVehicleData] = useState([]);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  // Initial filter config - will be updated based on user type
  const [filterConfig, setFilterConfig] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState("Newest First");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeVehicleTableTab, setActiveVehicleTableTab] = useState("overview");
  const [showCreateVehicle, setShowCreateVehicle] = useState(false);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [selectedSpeedIncidentKeys, setSelectedSpeedIncidentKeys] = useState([]);
  const [visibleVehicleCount, setVisibleVehicleCount] = useState(VEHICLE_TABLE_BATCH_SIZE);
  const [speedIncidentRows, setSpeedIncidentRows] = useState([]);
  const [isSpeedIncidentLoading, setIsSpeedIncidentLoading] = useState(false);
  const [speedIncidentDateOption, setSpeedIncidentDateOption] = useState("today");
  const [speedIncidentRouteFilter, setSpeedIncidentRouteFilter] = useState("");
  const [speedIncidentSpeedFilter, setSpeedIncidentSpeedFilter] = useState("");
  const [visibleSpeedIncidentCount, setVisibleSpeedIncidentCount] = useState(
    SPEED_INCIDENT_TABLE_BATCH_SIZE,
  );
  const [fleetMetrics, setFleetMetrics] = useState(EMPTY_FLEET_METRICS);
  const [fleetMetricTrends, setFleetMetricTrends] = useState({});
  const [fleetMetricLoading, setFleetMetricLoading] = useState({
    runtime: true,
    trips: true,
  });
  const [liveLocationsByVehicleId, setLiveLocationsByVehicleId] = useState({});
  const [locationNamesByVehicleId, setLocationNamesByVehicleId] = useState({});
  const [monitorDevicesByVehicleId, setMonitorDevicesByVehicleId] = useState({});
  const [previousSensorValuesByVehicleId, setPreviousSensorValuesByVehicleId] =
    useState({});
  const previousLiveSnapshotRef = useRef({
    locations: {},
    devices: {},
    fingerprint: "",
  });
  const [liveTelemetryVersion, setLiveTelemetryVersion] = useState(0);
  const [dashboardOverspeedCount, setDashboardOverspeedCount] = useState(0);
  const [dashboardDateOption, setDashboardDateOption] = useState("today");
  const [dashboardCustomRange, setDashboardCustomRange] = useState({ start: "", end: "" });
  const [dashboardError, setDashboardError] = useState("");
  const navigate = useNavigate();
  const handleDashboardDateRangeChange = useCallback((range) => {
    setDashboardError("");
    if (range?.type === "custom") {
      setDashboardDateOption(CUSTOM_DATE_OPTION);
      setDashboardCustomRange({
        start: range.startDate || "",
        end: range.endDate || "",
      });
      return;
    }

    setDashboardDateOption(toReportDateOption(range?.value || range?.label));
    setDashboardCustomRange({ start: "", end: "" });
  }, []);
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);
  const handleFilterChange = useCallback((filterValues) => {
    setFilters(filterValues);
  }, []);
  const handleVehicleTableTabChange = useCallback((value) => {
    setActiveVehicleTableTab(value);
  }, []);
  const { hasPermission } = usePermission();
  const { settings } = useSettings();
  const speedThresholdOptions = useMemo(() => {
    const limit = Number(settings?.maxSpeed) > 0 ? Number(settings.maxSpeed) : DEFAULT_OVERSPEED_LIMIT;
    return buildSpeedThresholdOptions(settings?.maxSpeed).map((option) => {
      if (!option.value) return option;
      const value = Number(option.value);
      return {
        ...option,
        label:
          value === Math.round(limit)
            ? t("Above limit ({{value}} km/h)", { value })
            : t("Above {{value}} km/h", { value }),
      };
    });
  }, [settings?.maxSpeed, t]);
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const useModuleLayout = useJsonLayout && !isRouteEyeAdmin();
  const { layout: moduleLayout } = usePageLayout(
    useModuleLayout ? PAGE_IDS.FLEET_VEHICLES : null,
  );

  const canViewDrivers = hasPermission("/api/driver/organization", "GET");
  const canViewSites = hasPermission("/api/site/organization", "GET");

  const layoutHeaders = useMemo(() => {
    if (!moduleLayout) return null;
    return buildTableHeadersFromLayout(moduleLayout, { hasPermission, t });
  }, [moduleLayout, hasPermission, t]);

  const layoutRowActions = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetRowActionsFromLayout(moduleLayout, { hasPermission });
  }, [moduleLayout, hasPermission]);

  const layoutStatCards = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetStatCards(moduleLayout, vehicleData);
  }, [moduleLayout, vehicleData]);

  const actionBarWidgets = useMemo(() => {
    if (!moduleLayout) return [];
    return getActionBarWidgets(moduleLayout);
  }, [moduleLayout]);

  const layoutFilterWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFilterWidgetsFromLayout(moduleLayout);
  }, [moduleLayout]);

  // Choose headers based on user
  const dynamicHeaders = isRouteEyeAdmin()
    ? getAdminHeaders()
    : layoutHeaders || getHeaders(canViewDrivers, canViewSites);

  // State for route options
  const [routeOptions, setRouteOptions] = useState([]);

  // Initialize filter config based on user type
  useEffect(() => {
    if (layoutFilterWidgets && !isRouteEyeAdmin()) {
      setFilterConfig(
        layoutFilterWidgets.map((widget) => ({
          id: widget.filterKey,
          label: widget.labelKey,
          helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
          options:
            widget.filterKey === "route"
              ? routeOptions
              : (widget.options || []).map((option) => ({
                  value: option.value,
                  label: option.label,
                })),
        })),
      );
      return;
    }

    if (isRouteEyeAdmin()) {
      // Admin filters: Organization, Site, Routes, Status
      setFilterConfig([
        {
          id: "organization",
          label: "Organization",
          helperText: "Select organization",
          options: organizationOptions,
        },
        {
          id: "site",
          label: "Site",
          helperText: "Select site",
          options: siteOptions,
        },
        {
          id: "route",
          label: "Routes",
          helperText: "Select route",
          options: routeOptions,
        },
        {
          id: "status",
          label: "Status",
          helperText: "Select vehicle status",
          options: [
            { value: "ACTIVE", label: "active" },
            { value: "INACTIVE", label: "inactive" },
            // { value: "DEAD", label: "inactive" },
          ],
        },
      ]);
    } else {
      // Regular user filters: Route, Status
      setFilterConfig([
        {
          id: "route",
          label: "Routes",
          helperText: "Select route",
          options: routeOptions,
        },
        {
          id: "status",
          label: "Status",
          helperText: "Select vehicle status",
          options: [
            { value: "ACTIVE", label: "active" },
            { value: "INACTIVE", label: "inactive" },
            // { value: "DEAD", label: "inactive" },
          ],
        },
      ]);
    }
  }, [organizationOptions, siteOptions, routeOptions, layoutFilterWidgets]);

  const getCustomActions = () =>
    Object.entries(FLEET_VEHICLE_ROW_ACTIONS)
      .filter(([, action]) => hasLayoutPermission(action.permission, hasPermission))
      .map(([, action]) => action);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    if (isRouteEyeAdmin()) {
      fetchVehiclesForAdmin();
      fetchOrganizationsAndSites();
    } else {
      fetchVehiclesForOrganization();
    }
  }, [navigate]);

  // Live speed/location from GPS trackers + sensor cards from device monitor,
  // same sources Monitor Vehicles uses.
  useEffect(() => {
    if (organizationInactive || !vehicleData.length) {
      setLiveLocationsByVehicleId({});
      setLocationNamesByVehicleId({});
      setMonitorDevicesByVehicleId({});
      setPreviousSensorValuesByVehicleId({});
      previousLiveSnapshotRef.current = { locations: {}, devices: {}, fingerprint: "" };
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const refreshLiveTelemetry = async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      const organizationId = filters.organization || getOrganizationId();

      try {
        const [locationEntries, readingsResponse, statusResponse] = await Promise.all([
          Promise.all(
            vehicleData
              .filter((vehicle) => vehicle?.device_id)
              .map(async (vehicle) => {
                try {
                  const { data } = await axios.get(
                    `${config.baseURL}/location/latest/${vehicle.device_id}`,
                    { withCredentials: true },
                  );
                  if (!data) return [String(vehicle.id), null];

                  const latitude = Number(data.latitude);
                  const longitude = Number(data.longitude);
                  const extractedSpeed = extractLiveTelemetry(data).speed;
                  const previousSpeed =
                    previousLiveSnapshotRef.current.locations?.[String(vehicle.id)]
                      ?.speed;
                  const speed =
                    extractedSpeed != null && Number.isFinite(Number(extractedSpeed))
                      ? Number(extractedSpeed)
                      : Number.isFinite(Number(previousSpeed))
                        ? Number(previousSpeed)
                        : null;
                  const telemetry = pickNonNullTelemetry(extractLiveTelemetry(data));

                  return [
                    String(vehicle.id),
                    {
                      latitude: Number.isFinite(latitude) ? latitude : null,
                      longitude: Number.isFinite(longitude) ? longitude : null,
                      speed: Number.isFinite(speed) ? speed : null,
                      timestamp: data.createdAt || data.timestamp || null,
                      telemetry,
                    },
                  ];
                } catch (error) {
                  console.warn(
                    `Failed to load latest location for vehicle ${vehicle.id}:`,
                    error,
                  );
                  return [String(vehicle.id), null];
                }
              }),
          ),
          getDeviceMonitorLatest({ organizationId }).catch((error) => {
            console.warn("Failed to load device monitor readings:", error);
            return { data: [] };
          }),
          axios
            .get(
              isRouteEyeAdmin()
                ? `${config.baseURL}/vehicle`
                : `${config.baseURL}/vehicle/organization`,
              { withCredentials: true },
            )
            .catch((error) => {
              console.warn("Failed to load vehicle statuses:", error);
              return { data: [] };
            }),
        ]);

        if (cancelled) return;

        const nextLocations = Object.fromEntries(
          locationEntries.filter(([, value]) => value),
        );
        const readings = readingsResponse?.data || [];

        // GPS monitor readings often nest temp/humidity/voltage inside `value`.
        readings.forEach((reading) => {
          if (!reading?.vehicleId) return;
          const vehicleId = String(reading.vehicleId);
          const telemetry = pickNonNullTelemetry(extractLiveTelemetry(reading));
          if (!Object.keys(telemetry).length) return;

          if (nextLocations[vehicleId]) {
            nextLocations[vehicleId] = {
              ...nextLocations[vehicleId],
              telemetry: {
                ...telemetry,
                ...(nextLocations[vehicleId].telemetry || {}),
              },
            };
          } else {
            nextLocations[vehicleId] = {
              latitude: null,
              longitude: null,
              speed: 0,
              timestamp: reading.recordedAt || null,
              telemetry,
            };
          }
        });

        const monitorRows = buildVehicleMonitorRows(vehicleData, readings);
        const nextDevices = {};

        monitorRows.forEach((row) => {
          nextDevices[String(row.id)] = row.devices || [];
        });

        const apiVehicles = Array.isArray(statusResponse?.data)
          ? statusResponse.data
          : statusResponse?.data?.vehicles || [];
        const apiStatusById = Object.fromEntries(
          apiVehicles.map((item) => [String(item.id), item.status || null]),
        );
        const hasStatusUpdates = vehicleData.some((vehicle) => {
          const apiStatus = apiStatusById[String(vehicle.id)];
          return apiStatus && apiStatus !== vehicle.status;
        });

        if (hasStatusUpdates) {
          setVehicleData((previous) =>
            previous.map((vehicle) => {
              const apiStatus = apiStatusById[String(vehicle.id)];
              if (!apiStatus || apiStatus === vehicle.status) return vehicle;
              return { ...vehicle, status: apiStatus };
            }),
          );
        }

        const nextFingerprint = fingerprintLiveSnapshot(nextLocations, nextDevices);
        if (nextFingerprint === previousLiveSnapshotRef.current.fingerprint) {
          return;
        }

        const previousSnapshot = previousLiveSnapshotRef.current;
        const previousValues = {};

        Object.entries(previousSnapshot.devices || {}).forEach(([vehicleId, devices]) => {
          previousValues[vehicleId] = { ...(previousValues[vehicleId] || {}) };
          (devices || []).forEach((device) => {
            if (!isLiveSensorDevice(device)) return;
            previousValues[vehicleId][device.id] = device.val;
          });
        });

        Object.entries(previousSnapshot.locations || {}).forEach(([vehicleId, location]) => {
          previousValues[vehicleId] = { ...(previousValues[vehicleId] || {}) };
          Object.entries(location?.telemetry || {}).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") return;
            previousValues[vehicleId][`gps-${key}`] = value;
          });
        });

        previousLiveSnapshotRef.current = {
          locations: nextLocations,
          devices: nextDevices,
          fingerprint: nextFingerprint,
        };

        setPreviousSensorValuesByVehicleId(previousValues);
        setLiveLocationsByVehicleId(nextLocations);
        setMonitorDevicesByVehicleId(nextDevices);
        setLiveTelemetryVersion((version) => version + 1);
      } catch (error) {
        console.error("Failed to refresh vehicle live telemetry:", error);
      }
    };

    const startPolling = () => {
      if (intervalId) return;
      refreshLiveTelemetry();
      intervalId = setInterval(refreshLiveTelemetry, LIVE_TELEMETRY_REFRESH_MS);
    };

    const stopPolling = () => {
      if (!intervalId) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopPolling();
        return;
      }
      startPolling();
    };

    if (typeof document === "undefined" || document.visibilityState !== "hidden") {
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [organizationInactive, vehicleData, filters.organization]);

  // Turn lat/lng into place names (cached + rate-limited Nominatim reverse geocode).
  useEffect(() => {
    if (organizationInactive || !vehicleData.length) return undefined;

    let cancelled = false;

    resolveVehicleLocationNames(vehicleData, {
      getCoordinates: (vehicle) => {
        const liveLocation = liveLocationsByVehicleId[String(vehicle.id)];
        return {
          latitude: liveLocation?.latitude ?? vehicle.latitude,
          longitude: liveLocation?.longitude ?? vehicle.longitude,
        };
      },
      onResolved: (vehicleId, address) => {
        if (cancelled || !address) return;
        const liveLocation = liveLocationsByVehicleId[String(vehicleId)];
        const sourceVehicle = vehicleData.find(
          (item) => String(item.id) === String(vehicleId),
        );
        const cacheKey = buildGeocodeCacheKey(
          liveLocation?.latitude ?? sourceVehicle?.latitude,
          liveLocation?.longitude ?? sourceVehicle?.longitude,
        );
        setLocationNamesByVehicleId((previous) => {
          const current = previous[vehicleId];
          if (current?.address === address && current?.cacheKey === cacheKey) {
            return previous;
          }
          return { ...previous, [vehicleId]: { address, cacheKey } };
        });
      },
    });

    return () => {
      cancelled = true;
    };
  }, [organizationInactive, vehicleData, liveLocationsByVehicleId]);

  // Distance, runtime and trip counts are derived from location history by the
  // report services; they are not columns on the vehicle record.
  // Trends compare each card value to the previous equivalent period (yesterday
  // when the selected range is "today").
  useEffect(() => {
    let cancelled = false;

    if (organizationInactive) {
      setFleetMetricLoading({ runtime: false, trips: false });
      setFleetMetrics(EMPTY_FLEET_METRICS);
      setFleetMetricTrends({});
      return undefined;
    }

      const fetchFleetMetrics = async (background = false) => {
      const isCustom = dashboardDateOption === CUSTOM_DATE_OPTION;
      if (isCustom && !(dashboardCustomRange.start && dashboardCustomRange.end)) {
        if (!cancelled) {
          setFleetMetricLoading({ runtime: false, trips: false });
          setFleetMetrics(EMPTY_FLEET_METRICS);
          setFleetMetricTrends({});
        }
        return;
      }

      const reportParams = {
        orgId: filters.organization || getOrganizationId(),
        siteId: filters.site || undefined,
        category: "vehicles",
        selectedIds: [],
        selectAll: true,
        dateOption: dashboardDateOption,
        customStartDate: isCustom ? dashboardCustomRange.start : undefined,
        customEndDate: isCustom ? dashboardCustomRange.end : undefined,
      };

      const comparisonPeriod = getComparisonPeriodParams(
        dashboardDateOption,
        dashboardCustomRange,
      );
      const comparisonParams = {
        ...reportParams,
        dateOption: comparisonPeriod.dateOption,
        customStartDate: comparisonPeriod.customStartDate,
        customEndDate: comparisonPeriod.customEndDate,
      };

      const fleetCacheKey = [
        reportParams.orgId || "",
        reportParams.siteId || "",
        dashboardDateOption,
        dashboardCustomRange.start || "",
        dashboardCustomRange.end || "",
      ].join("|");
      const cachedFleet = vehicleFleetCache.get(fleetCacheKey);
      const fleetCacheIsFresh =
        cachedFleet && Date.now() - cachedFleet.fetchedAt < VEHICLE_PAGE_CACHE_MS;
      if (cachedFleet) {
        if (!background) {
          setFleetMetrics(cachedFleet.metrics);
          setFleetMetricTrends(cachedFleet.trends);
          setFleetMetricLoading({ runtime: false, trips: false });
        }
        if (!background && fleetCacheIsFresh) return;
      } else if (!background) {
        setFleetMetricLoading({ runtime: true, trips: true });
      }

      const rememberFleet = (partialMetrics, partialTrends) => {
        const previous = vehicleFleetCache.get(fleetCacheKey) || {
          metrics: EMPTY_FLEET_METRICS,
          trends: {},
        };
        vehicleFleetCache.set(fleetCacheKey, {
          metrics: { ...previous.metrics, ...partialMetrics },
          trends: { ...previous.trends, ...partialTrends },
          fetchedAt: Date.now(),
        });
      };

      const loadRuntimeMetrics = async () => {
        try {
          const runtimeReport = await reportsApi.getRuntimeReport(reportParams);
          if (cancelled) return;
          const current = buildFleetMetrics(runtimeReport, null);
          const currentRuntime = {
            distanceCovered: current.distanceCovered,
            totalRuntime: current.totalRuntime,
            movingHours: current.movingHours,
            vehiclesRunning: current.vehiclesRunning,
            vehiclesNeedAttention: current.vehiclesNeedAttention,
            vehiclesTotal: current.vehiclesTotal,
          };
          setFleetMetrics((metrics) => ({ ...metrics, ...currentRuntime }));
          rememberFleet(currentRuntime, {});
          if (!cancelled) {
            setFleetMetricLoading((loading) => ({ ...loading, runtime: false }));
          }

          const previousReport = await reportsApi.getRuntimeReport(comparisonParams).catch((err) => {
            console.error("Failed to load previous fleet runtime metrics:", err);
            return null;
          });
          const previous = buildFleetMetrics(previousReport, null);
          if (cancelled) return;
          const runtimeTrends = {
            vehiclesRunning: computePercentChangeTrend(current.vehiclesRunning, previous.vehiclesRunning),
            vehiclesNeedAttention: computePercentChangeTrend(current.vehiclesNeedAttention, previous.vehiclesNeedAttention),
            distanceCovered: computePercentChangeTrend(current.distanceCovered, previous.distanceCovered),
            totalRuntime: computePercentChangeTrend(current.totalRuntime, previous.totalRuntime),
          };
          setFleetMetricTrends((trends) => ({ ...trends, ...runtimeTrends }));
          rememberFleet({}, runtimeTrends);
        } catch (err) {
          console.error("Failed to load fleet runtime metrics:", err);
          if (!cancelled) {
            const backendMsg =
              err?.response?.data?.error ||
              err?.response?.data?.message;
            if (backendMsg) {
              setDashboardError(backendMsg);
            }
          }
        } finally {
          if (!cancelled) {
            setFleetMetricLoading((loading) => ({ ...loading, runtime: false }));
          }
        }
      };

      const loadTripMetrics = async () => {
        const currentTripParams = {
          ...reportParams,
          ...(getExactDayReportParams(reportParams.dateOption) || {}),
        };
        const previousTripParams = {
          ...comparisonParams,
          ...(getExactDayReportParams(comparisonParams.dateOption) || {}),
        };
        try {
          const tripReport = await reportsApi.getTripReport(currentTripParams);
          if (cancelled) return;
          const current = buildFleetMetrics(null, tripReport);
          const currentTrips = {
            tripsStarted: current.tripsStarted,
            completedTrips: current.completedTrips,
            onTimePerformance: current.onTimePerformance,
          };
          setFleetMetrics((metrics) => ({ ...metrics, ...currentTrips }));
          rememberFleet(currentTrips, {});
          if (!cancelled) {
            setFleetMetricLoading((loading) => ({ ...loading, trips: false }));
          }

          const previousReport = await reportsApi.getTripReport(previousTripParams).catch((err) => {
            console.error("Failed to load previous fleet trip metrics:", err);
            return null;
          });
          const previous = buildFleetMetrics(null, previousReport);
          if (cancelled) return;
          const tripTrends = {
            tripsStarted: computePercentChangeTrend(current.tripsStarted, previous.tripsStarted),
            onTimePerformance: computePerformanceDifferenceTrend(
              current.onTimePerformance,
              previous.onTimePerformance,
            ),
          };
          setFleetMetricTrends((trends) => ({ ...trends, ...tripTrends }));
          rememberFleet({}, tripTrends);
        } catch (err) {
          console.error("Failed to load fleet trip metrics:", err);
          if (!cancelled) {
            const backendMsg =
              err?.response?.data?.error ||
              err?.response?.data?.message;
            if (backendMsg) {
              setDashboardError(backendMsg);
            }
          }
        } finally {
          if (!cancelled) {
            setFleetMetricLoading((loading) => ({ ...loading, trips: false }));
          }
        }
      };

      loadRuntimeMetrics();
      loadTripMetrics();
    };

    fetchFleetMetrics();
    const refreshTimer = dashboardRangeIncludesToday(dashboardDateOption, dashboardCustomRange)
      ? window.setInterval(() => {
          if (!cancelled) fetchFleetMetrics(true);
        }, VEHICLE_PAGE_CACHE_MS)
      : null;

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [
    organizationInactive,
    filters.organization,
    filters.site,
    dashboardDateOption,
    dashboardCustomRange.start,
    dashboardCustomRange.end,
  ]);

  useEffect(() => {
    if (activeVehicleTableTab !== "speedIncidents") return;

    let cancelled = false;
    const fetchSpeedIncidents = async (background = false) => {
      const speedCacheKey = [
        filters.organization || getOrganizationId() || "",
        filters.site || "",
        speedIncidentDateOption,
      ].join("|");
      const cachedSpeed = vehicleSpeedCache.get(speedCacheKey);
      const speedCacheIsFresh =
        cachedSpeed && Date.now() - cachedSpeed.fetchedAt < VEHICLE_PAGE_CACHE_MS;
      if (cachedSpeed && !background) {
        setSpeedIncidentRows(cachedSpeed.rows);
        setIsSpeedIncidentLoading(false);
      }
      if (!background && speedCacheIsFresh) return;
      if (!background) setIsSpeedIncidentLoading(!cachedSpeed);
      if (!background && cachedSpeed) setSpeedIncidentRows(cachedSpeed.rows);
      try {
        const selectedOrganizationId = filters.organization || getOrganizationId();
        const report = await reportsApi.getOverspeedReport({
          orgId: selectedOrganizationId,
          siteId: filters.site || undefined,
          category: "vehicles",
          selectedIds: [],
          selectAll: true,
          dateOption: speedIncidentDateOption,
        });

        if (!cancelled) {
          const rows = mapOverspeedReportRows(getReportRows(report));
          vehicleSpeedCache.set(speedCacheKey, { rows, fetchedAt: Date.now() });
          setSpeedIncidentRows(rows);
        }
      } catch (err) {
        console.error("Failed to load overspeed incidents:", err);
        if (!cancelled && !background) setSpeedIncidentRows([]);
      } finally {
        if (!cancelled) setIsSpeedIncidentLoading(false);
      }
    };

    fetchSpeedIncidents();
    const refreshTimer = dashboardRangeIncludesToday(speedIncidentDateOption)
      ? window.setInterval(() => {
          if (!cancelled) fetchSpeedIncidents(true);
        }, VEHICLE_PAGE_CACHE_MS)
      : null;

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [activeVehicleTableTab, filters.organization, filters.site, speedIncidentDateOption]);

  // Overspeed count for Vehicle Alerts Summary (same report source as Speed Incidents).
  useEffect(() => {
    let cancelled = false;
    const isCustom = dashboardDateOption === CUSTOM_DATE_OPTION;

    if (organizationInactive) {
      setDashboardOverspeedCount(0);
      return undefined;
    }

    if (isCustom && !(dashboardCustomRange.start && dashboardCustomRange.end)) {
      setDashboardOverspeedCount(0);
      return undefined;
    }

    const fetchDashboardOverspeedCount = async (background = false) => {
      const countCacheKey = [
        filters.organization || getOrganizationId() || "",
        filters.site || "",
        dashboardDateOption,
        dashboardCustomRange.start || "",
        dashboardCustomRange.end || "",
      ].join("|");
      const cachedCount = vehicleOverspeedCountCache.get(countCacheKey);
      const countCacheIsFresh =
        cachedCount && Date.now() - cachedCount.fetchedAt < VEHICLE_PAGE_CACHE_MS;
      if (!background && cachedCount) setDashboardOverspeedCount(cachedCount.count);
      if (!background && countCacheIsFresh) return;
      try {
        const report = await reportsApi.getOverspeedReport({
          orgId: filters.organization || getOrganizationId(),
          siteId: filters.site || undefined,
          category: "vehicles",
          selectedIds: [],
          selectAll: true,
          dateOption: dashboardDateOption,
          customStartDate: isCustom ? dashboardCustomRange.start : undefined,
          customEndDate: isCustom ? dashboardCustomRange.end : undefined,
        });
        if (!cancelled) {
          const count = getReportRows(report).length;
          vehicleOverspeedCountCache.set(countCacheKey, { count, fetchedAt: Date.now() });
          setDashboardOverspeedCount(count);
        }
      } catch (error) {
        console.error("Failed to load dashboard overspeed count:", error);
        if (!cancelled && !background) {
          setDashboardOverspeedCount(0);
          const backendMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message;
          if (backendMsg) {
            setDashboardError(backendMsg);
          }
        }
      }
    };

    fetchDashboardOverspeedCount();
    const refreshTimer = dashboardRangeIncludesToday(dashboardDateOption, dashboardCustomRange)
      ? window.setInterval(() => {
          if (!cancelled) fetchDashboardOverspeedCount(true);
        }, VEHICLE_PAGE_CACHE_MS)
      : null;

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [
    organizationInactive,
    filters.organization,
    filters.site,
    dashboardDateOption,
    dashboardCustomRange.start,
    dashboardCustomRange.end,
  ]);

  // Fetch organizations and sites for admin
  const fetchOrganizationsAndSites = async () => {
    try {
      // Fetch organizations
      const orgResponse = await axios.get(`${config.baseURL}/organization`, {
        withCredentials: true,
      });

      let organizations = [];
      if (Array.isArray(orgResponse.data)) {
        organizations = orgResponse.data;
      } else if (Array.isArray(orgResponse.data.organizations)) {
        organizations = orgResponse.data.organizations;
      }

      const orgOptions = organizations.map((org) => ({
        value: org.id.toString(),
        label: org.name || `Organization ${org.id}`,
      }));
      setOrganizationOptions(orgOptions);

      // Fetch sites
      const siteResponse = await axios.get(`${config.baseURL}/site`, {
        withCredentials: true,
      });

      let sites = [];
      if (Array.isArray(siteResponse.data)) {
        sites = siteResponse.data;
      } else if (Array.isArray(siteResponse.data.sites)) {
        sites = siteResponse.data.sites;
      }

      const siteOptions = sites.map((site) => ({
        value: site.id.toString(),
        label: site.name || site.siteId || `Site ${site.id}`,
      }));
      setSiteOptions(siteOptions);

      // Fetch routes
      const routeResponse = await axios.get(`${config.baseURL}/route`, {
        withCredentials: true,
      });

      let routes = [];
      if (Array.isArray(routeResponse.data)) {
        routes = routeResponse.data;
      } else if (Array.isArray(routeResponse.data.routes)) {
        routes = routeResponse.data.routes;
      }

      const routeOpts = routes.map((route) => ({
        value: route.id.toString(),
        label: route.name || `Route ${route.id}`,
      }));
      setRouteOptions(routeOpts);
    } catch (error) {
      console.error("Error fetching organizations/sites/routes:", error);
    }
  };

  const fetchVehiclesForAdmin = () => {
    axios
      .get(`${config.baseURL}/vehicle`, { withCredentials: true })
      .then((response) => {
        let vehicles = [];
        if (Array.isArray(response.data)) {
          vehicles = response.data;
        } else if (Array.isArray(response.data.vehicles)) {
          vehicles = response.data.vehicles;
        }
        setVehicleData(vehicles);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
          navigate("/");
        }
      });
  };

  const fetchVehiclesForOrganization = (force = false) => {
    const cacheKey = String(getOrganizationId() || "");
    if (force) vehicleListCache.delete(cacheKey);
    const cachedList = vehicleListCache.get(cacheKey);
    if (cachedList && Date.now() - cachedList.fetchedAt < VEHICLE_PAGE_CACHE_MS) {
      setVehicleData(cachedList.vehicles);
      setRouteOptions(cachedList.routeOptions);
      setIsLoading(false);
      return;
    }
    axios
      .get(`${config.baseURL}/vehicle/organization`, { withCredentials: true })
      .then((response) => {
        if (!Array.isArray(response.data)) {
          setVehicleData([]);
          setIsLoading(false);
          return;
        }

        setVehicleData(response.data);
        vehicleListCache.set(cacheKey, {
          vehicles: response.data,
          routeOptions: [],
          fetchedAt: Date.now(),
        });

        // Extract unique routes for filter
        const uniqueRoutes = [];
        const routeIds = new Set();
        response.data.forEach((vehicle) => {
          const routeId =
            vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;
          const routeName =
            vehicle.route?.name || vehicle.route?.Name || `Route ${routeId}`;
          if (routeId && !routeIds.has(routeId)) {
            routeIds.add(routeId);
            uniqueRoutes.push({
              value: routeId.toString(),
              label: routeName,
            });
          }
        });

        setRouteOptions(uniqueRoutes);
        vehicleListCache.set(cacheKey, {
          vehicles: response.data,
          routeOptions: uniqueRoutes,
          fetchedAt: Date.now(),
        });
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
          navigate("/");
        }
      });
  };

  const formatDataForTable = (data) => {
    return data.map((vehicle) => {
      const formattedData = {
        vehicleName: vehicle.name || "N/A",
        VehicleNumber: vehicle.VehicleNumber || "N/A",
        routes: vehicle.route?.name || "N/A",
        siteID: vehicle.site?.siteId || "N/A",
        organizationName: vehicle.organization?.name || "N/A",
        drivers: vehicle.driver
          ? `${vehicle.driver.first_name || ""} ${
              vehicle.driver.last_name || ""
            }`.trim()
          : "N/A",
        status: (
          <div className="status-emoji">
            {vehicle.status === "ACTIVE" && (
              <img src={activeEmoji} alt="ACTIVE" title="Active Vehicle" />
            )}
            {/* {vehicle.status === "INACTIVE" && (
              <img
                src={inActiveEmoji}
                alt="INACTIVE"
                title="Inactive Vehicle"
              />
            )}
            {(vehicle.status === "DEAD" || !vehicle.status) && (
              <img src={inActiveEmoji} alt="DEAD" title="Inactive Vehicle" />
            )} */}
            {(vehicle.status === "INACTIVE" ||
              vehicle.status === "DEAD" ||
              !vehicle.status) && (
              <img
                src={inActiveEmoji}
                alt="INACTIVE"
                title="Inactive Vehicle"
              />
            )}
          </div>
        ),
        id: vehicle.id,
        originalData: vehicle,
      };

      return formattedData;
    });
  };

  // Handle search changes
  // (stable callbacks defined above near dashboard date handlers)

  const filteredVehicleData = useMemo(
    () =>
      vehicleData.filter((vehicle) => {
    // Admin filters: organization, site, route, status
    if (isRouteEyeAdmin()) {
      const organizationMatch =
        !filters.organization ||
        (vehicle.organization?.id &&
          String(vehicle.organization.id) === String(filters.organization));

      const siteMatch =
        !filters.site ||
        (vehicle.site?.id && String(vehicle.site.id) === String(filters.site));

      const routeId =
        vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;
      const routeMatch =
        !filters.route ||
        (routeId && String(routeId) === String(filters.route));

      let statusMatch = true;
      if (filters.status) {
        if (filters.status === "INACTIVE") {
          statusMatch =
            vehicle.status === "INACTIVE" ||
            vehicle.status === "DEAD" ||
            !vehicle.status;
        } else {
          statusMatch = vehicle.status === filters.status && vehicle.status;
        }
      }

      if (!organizationMatch || !siteMatch || !routeMatch || !statusMatch) {
        return false;
      }
    } else {
      // Regular user filters: route, status
      const routeId =
        vehicle.route?.Id || vehicle.route?.id || vehicle.route?.ID;

      const routeMatch =
        !filters.route ||
        (routeId && String(routeId) === String(filters.route));

      let statusMatch = true;
      if (filters.status) {
        if (filters.status === "INACTIVE") {
          statusMatch =
            vehicle.status === "INACTIVE" ||
            vehicle.status === "DEAD" ||
            !vehicle.status;
        } else {
          statusMatch = vehicle.status === filters.status && vehicle.status;
        }
      }

      if (!routeMatch || !statusMatch) {
        return false;
      }
    }

    // Search query filter
    if (!searchQuery) {
      return true;
    }

    const query = searchQuery.toLowerCase();
    return (
      (vehicle.VehicleNumber &&
        vehicle.VehicleNumber.toLowerCase().includes(query)) ||
      (vehicle.name && vehicle.name.toLowerCase().includes(query)) ||
      (vehicle.site?.siteId &&
        vehicle.site.siteId.toLowerCase().includes(query)) ||
      (vehicle.organization?.name &&
        vehicle.organization.name.toLowerCase().includes(query)) ||
      (vehicle.route?.name &&
        vehicle.route.name.toLowerCase().includes(query)) ||
      (vehicle.driver?.first_name &&
        vehicle.driver.first_name.toLowerCase().includes(query)) ||
      (vehicle.driver?.last_name &&
        vehicle.driver.last_name.toLowerCase().includes(query)) ||
      (vehicle.status && vehicle.status.toLowerCase().includes(query))
    );
      }),
    [vehicleData, filters, searchQuery],
  );

  const visibleVehicleData = useMemo(() => {
    let result = filteredVehicleData;
    if (filters.vehicleType) {
      result = filteredVehicleData.filter((vehicle) => {
        const vehicleType =
          vehicle.VehicleType ||
          vehicle.vehicleType ||
          vehicle.type ||
          vehicle.category ||
          "N/A";
        return String(vehicleType) === String(filters.vehicleType);
      });
    }

    return [...result].sort((a, b) => {
      const newestFirst = sortBy === "Newest First";
      const timeA = new Date(
        a.createdAt || a.created_at || a.createdDate || a.created_date || 0,
      ).getTime();
      const timeB = new Date(
        b.createdAt || b.created_at || b.createdDate || b.created_date || 0,
      ).getTime();

      if (timeA !== timeB) {
        return newestFirst ? timeB - timeA : timeA - timeB;
      }

      const idA = Number(a.id || 0);
      const idB = Number(b.id || 0);
      return newestFirst ? idB - idA : idA - idB;
    });
  }, [filteredVehicleData, filters.vehicleType, sortBy]);

  const handleEntryExitRegister = () => {
    navigate("/vehicle/entry-exit");
  };

  // Track all vehicles handler
  const handleTrackAllVehicles = () => {
    navigate("/vehicle/TrackAllVehicle", {
      state: { allVehicleData: vehicleData },
    });
  };

  // Add vehicle handler
  const handleAddVehicle = () => {
    if (hasPermission("/api/vehicle", "POST")) {
      setShowCreateVehicle((isOpen) => !isOpen);
      setExpandedVehicleId(null);
    } else {
      console.log("User doesn't have permission to add vehicles");
    }
  };

  const refreshVehicles = () => {
    setIsLoading(true);
    if (isRouteEyeAdmin()) {
      fetchVehiclesForAdmin();
    } else {
      fetchVehiclesForOrganization(true);
    }
  };

  const handleCreateVehicleComplete = () => {
    setShowCreateVehicle(false);
    refreshVehicles();
  };

  const toggleExpandedVehicleRow = (rowVehicleId) => {
    setShowCreateVehicle(false);
    setExpandedVehicleId((currentId) =>
      String(currentId || "") === String(rowVehicleId || "") ? null : rowVehicleId,
    );
  };

  const handleUpdateVehicleComplete = () => {
    setExpandedVehicleId(null);
    refreshVehicles();
  };

  // Delete modal handlers
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (vehicleToDelete) {
      if (!hasPermission("/api/vehicle", "DELETE")) {
        console.log("User doesn't have permission to delete vehicles");
        setIsDeleteModalOpen(false);
        setVehicleToDelete(null);
        return;
      }
      try {
        const userState = localStorage.getItem("userState");
        const parsedUserState = JSON.parse(userState);
        const orgId = parsedUserState.user.organizationId;
        const response = await axios.delete(`${config.baseURL}/vehicle`, {
          data: { vehicleId: vehicleToDelete.id, organizationId: orgId },
        });
        console.log("Vehicle deleted successfully:", response.data);
        setExpandedVehicleId(null);
        refreshVehicles();
      } catch (error) {
        console.log("Error in deleting vehicle data: ", error);
        throw error;
      }
    }
    setIsDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  const handleVehicleAction = (actionId, vehicle, actionObj = {}) => {
    switch (actionId) {
      case "update":
        if (hasPermission("/api/vehicle", "PUT")) {
          navigate(actionObj.path || "/vehicle/add", { state: vehicle.id });
        }
        break;
      case "track":
      case "monitor":
        if (hasPermission("/api/vehicle/id", "GET")) {
          navigate(actionObj.path || "/vehicle/track", { state: vehicle.id });
        }
        break;
      case "analytics":
        if (hasPermission("/api/vehicle/id", "GET")) {
          navigate(`/vehicle/track?vehicleId=${vehicle.id}`, {
            state: { vehicleId: vehicle.id, initialTab: "analytics" },
          });
        }
        break;
      case "delete":
        if (hasPermission("/api/vehicle", "DELETE")) {
          setVehicleToDelete(vehicle);
          setIsDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const handleActionClick = (actionId, rowIndex, actionObj) => {
    const vehicle =
      filteredVehicleData[rowIndex].originalData ||
      filteredVehicleData[rowIndex];

    handleVehicleAction(actionId, vehicle, actionObj);
  };

  const legacyRowActions = getCustomActions();
  const tableActions = layoutRowActions || legacyRowActions;

  const canUpdateVehicle = tableActions.some((action) => action.id === "update");
  const canDeleteVehicle = tableActions.some((action) => action.id === "delete");
  const canMonitorVehicle = tableActions.some((action) => action.id === "track");

  const showActionsColumn =
    layoutRowActions?.length > 0 ||
    hasPermission("/api/vehicle", "PUT") ||
    hasPermission("/api/vehicle/id", "GET") ||
    hasPermission("/api/vehicle", "DELETE");

  const filteredHeaders = showActionsColumn
    ? dynamicHeaders
    : dynamicHeaders.filter((h) => h.key !== ACTIONS_COLUMN_ID);

  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createVehicle");
  const monitorAction = actionBarWidgets.find((widget) => widget.actionKey === "monitorVehicles");
  const entryExitAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "entryExitRegister",
  );

  const canAddVehicle = createAction
    ? hasLayoutPermission(createAction.permission, hasPermission)
    : hasPermission("/api/vehicle", "POST");

  const pageTitle = getPageTitleKey(moduleLayout) || "Vehicles";
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;

  const legacyStatCards = [
    {
      title: "totalVehicles",
      value: vehicleData?.length || 0,
      backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "activeVehicles",
      value: vehicleData?.filter((vehicle) => vehicle.status === "ACTIVE").length || 0,
      backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "inactiveVehicles",
      value:
        vehicleData?.filter(
          (vehicle) =>
            vehicle.status === "INACTIVE" || vehicle.status === "DEAD" || !vehicle.status,
        ).length || 0,
      backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
      titleColor: TEXT_COLORS.BLACK,
      valueColor: TEXT_COLORS.BLACK,
    },
  ];

  const {
    distanceCovered,
    totalRuntime,
    tripsStarted,
    onTimePerformance,
    vehiclesRunning,
    vehiclesNeedAttention,
    vehiclesTotal,
  } = fleetMetrics;
  const formatNumber = (value, digits = 0) =>
    Number(value || 0).toLocaleString(undefined, {
      maximumFractionDigits: digits,
    });

  const vehicleStatsCards = [
    {
      title: "Vehicles Running",
      value: `${formatNumber(vehiclesRunning)} / ${formatNumber(vehiclesTotal)}`,
      icon: DirectionsCarIcon,
      tone: "blue",
      trend: fleetMetricTrends.vehiclesRunning?.trend,
      trendDirection: fleetMetricTrends.vehiclesRunning?.trendDirection,
      loadingSource: "runtime",
    },
    {
      title: "Vehicles Need Attention",
      value: formatNumber(vehiclesNeedAttention),
      icon: WarningAmberIcon,
      tone: "red",
      trend: fleetMetricTrends.vehiclesNeedAttention?.trend,
      trendDirection: fleetMetricTrends.vehiclesNeedAttention?.trendDirection,
      loadingSource: "runtime",
    },
    {
      title: "Trips Started",
      value: formatNumber(tripsStarted),
      icon: PlayCircleOutlineIcon,
      tone: "amber",
      trend: fleetMetricTrends.tripsStarted?.trend,
      trendDirection: fleetMetricTrends.tripsStarted?.trendDirection,
      loadingSource: "trips",
    },
    {
      title: "On Time Performance",
      value: onTimePerformance === null ? "N/A" : `${onTimePerformance}%`,
      icon: AccessTimeIcon,
      tone: "green",
      trend: fleetMetricTrends.onTimePerformance?.trend,
      trendDirection: fleetMetricTrends.onTimePerformance?.trendDirection,
      loadingSource: "trips",
    },
    {
      title: "Distance Covered",
      value: `${formatNumber(distanceCovered, 1)} Km`,
      icon: AltRouteIcon,
      tone: "purple",
      trend: fleetMetricTrends.distanceCovered?.trend,
      trendDirection: fleetMetricTrends.distanceCovered?.trendDirection,
      loadingSource: "runtime",
    },
    {
      title: "Total Runtime",
      value: `${formatNumber(totalRuntime, 2)} Hrs`,
      icon: SpeedIcon,
      tone: "cyan",
      trend: fleetMetricTrends.totalRuntime?.trend,
      trendDirection: fleetMetricTrends.totalRuntime?.trendDirection,
      loadingSource: "runtime",
    },
  ];

  const getVehicleStatusLabel = (status) => {
    if (status === "ACTIVE") return "Active";
    if (status === "INACTIVE" || status === "DEAD" || !status) return "Inactive";
    return status;
  };

  const getVehicleStatusTone = (status) => {
    if (status === "ACTIVE") return "green";
    if (status === "DELAYED") return "amber";
    if (status === "OFFLINE") return "gray";
    if (status === "STOPPED" || status === "INACTIVE" || status === "DEAD" || !status) {
      return "red";
    }
    return "gray";
  };

  const getVehicleType = (vehicle) =>
    vehicle.VehicleType ||
    vehicle.vehicleType ||
    vehicle.type ||
    vehicle.category ||
    "N/A";

  const getVehicleSpeed = (vehicle) => {
    const liveLocation = liveLocationsByVehicleId[String(vehicle.id)];
    const rawSpeed =
      liveLocation?.speed ??
      getNumericMetric(vehicle, ["speed", "Speed", "currentSpeed", "latestSpeed"]) ??
      getNumericMetric(vehicle.latestLocation || {}, ["Speed", "speed"]);
    return getDisplayedVehicleSpeed(rawSpeed, vehicle.status);
  };

  const getDriverDetails = (vehicle) => {
    const driverName = vehicle.driver
      ? `${vehicle.driver.first_name || ""} ${vehicle.driver.last_name || ""}`.trim()
      : vehicle.driverName || vehicle.driver_name || "";
    const driverPhone =
      vehicle.driver?.phoneNumber ||
      vehicle.driver?.phone ||
      vehicle.driver?.mobile ||
      vehicle.driverPhone ||
      vehicle.driver_phone ||
      "";

    return {
      name: driverName || "N/A",
      phone: driverPhone,
    };
  };

  const getCurrentLocation = (vehicle) => {
    const vehicleId = String(vehicle.id);
    const liveLocation = liveLocationsByVehicleId[vehicleId];
    const latitude = liveLocation?.latitude ?? vehicle.latitude;
    const longitude = liveLocation?.longitude ?? vehicle.longitude;
    const cacheKey = buildGeocodeCacheKey(latitude, longitude);
    const namedLocation = locationNamesByVehicleId[vehicleId];
    if (namedLocation?.address && namedLocation?.cacheKey === cacheKey) {
      return namedLocation.address;
    }
    if (typeof namedLocation === "string" && namedLocation) {
      return namedLocation;
    }

    const formattedLatitude = formatCoordinate(latitude);
    const formattedLongitude = formatCoordinate(longitude);
    if (formattedLatitude && formattedLongitude) {
      return `${formattedLatitude}, ${formattedLongitude}`;
    }
    return "--";
  };

  const getLastUpdate = (vehicle) => {
    const liveLocation = liveLocationsByVehicleId[String(vehicle.id)];
    return (
      liveLocation?.timestamp ||
      vehicle.lastUpdate ||
      vehicle.lastUpdated ||
      vehicle.updatedAt ||
      vehicle.latestLocation?.timestamp ||
      vehicle.lastLocation?.timestamp ||
      ""
    );
  };

  const formatLastUpdate = (value) => {
    if (!value) return "--";
    const date =
      typeof value === "number"
        ? new Date(value < 1e12 ? value * 1000 : value)
        : new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 60000) return t("Just now");
    if (diffMs < 3600000) {
      return t("{{count}} min ago", { count: Math.floor(diffMs / 60000) });
    }
    return date.toLocaleDateString("en-US");
  };

  const getLiveDataCards = (vehicle) => {
    const vehicleId = String(vehicle.id);
    return buildLiveDataCards({
      vehicle,
      devices: monitorDevicesByVehicleId[vehicleId] || [],
      liveLocation: liveLocationsByVehicleId[vehicleId],
      previousSensorValues: previousSensorValuesByVehicleId[vehicleId] || {},
    });
  };

  const vehicleTypeOptions = Array.from(
    new Set(vehicleData.map(getVehicleType).filter((type) => type && type !== "N/A")),
  ).map((type) => ({ value: type, label: type }));

  const vehicleTableHeaders = [
    { key: "vehicleName", label: "Vehicle", width: "112px" },
    { key: "regNo", label: "Reg. No.", width: "132px" },
    { key: "vehicleType", label: "Type", width: "90px" },
    { key: "route", label: "Route", width: "130px" },
    {
      key: "driver",
      label: "Driver",
      width: "140px",
      render: (row) => (
        <div className="vehicle-new-driver-cell">
          <strong>{row.driverName}</strong>
          {row.driverPhone ? <span>{row.driverPhone}</span> : null}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "180px",
      render: (row) => (
        <div className="vehicle-new-status-cell">
          <TableStatusBadge tone={getVehicleStatusTone(row.originalStatus)}>
            {t(row.status)}
          </TableStatusBadge>
          <span>{row.speedLabel}</span>
        </div>
      ),
    },
    {
      key: "currentLocation",
      label: "Current Location",
      width: "200px",
      render: (row) => (
        <span className="vehicle-new-location-cell" title={row.currentLocation}>
          {row.currentLocation}
        </span>
      ),
    },
    {
      key: "lastUpdate",
      label: "Last Update",
      width: "130px",
      render: (row) => (
        <TableLastUpdate tone={row.lastUpdateTone}>{row.lastUpdate}</TableLastUpdate>
      ),
    },
    {
      key: "liveData",
      label: "Live Data",
      width: "160px",
      render: (row) =>
        row.liveDataCards?.length ? (
          <div className="vehicle-new-live-data">
            {row.liveDataCards.map((card) => (
              <div className="vehicle-new-live-metric" key={card.id}>
                <strong className={card.tone}>
                  {card.typeOnly || card.display == null
                    ? t(card.label)
                    : `${t(card.label)}: ${card.display}`}
                </strong>
                {card.delta ? <span className="delta">{card.delta}</span> : null}
              </div>
            ))}
          </div>
        ) : (
          "--"
        ),
    },
  ];

  const vehicleInventoryHeaders = [
    { key: "vehicleName", label: "Vehicle", width: "140px" },
    { key: "regNo", label: "Reg. No.", width: "132px" },
    { key: "vehicleType", label: "Type", width: "90px" },
    { key: "route", label: "Route", width: "160px" },
    {
      key: "driver",
      label: "Driver",
      width: "160px",
      render: (row) => (
        <div className="vehicle-new-driver-cell">
          <strong>{row.driverName}</strong>
          {row.driverPhone ? <span>{row.driverPhone}</span> : null}
        </div>
      ),
    },
  ];

  const vehicleInventoryData = useMemo(
    () =>
      visibleVehicleData.map((vehicle) => {
        const driverDetails = getDriverDetails(vehicle);
        return {
          id: vehicle.id,
          vehicleName: vehicle.name || vehicle.vehicleName || vehicle.VehicleName || "N/A",
          regNo: vehicle.VehicleNumber || vehicle.vehicleNumber || "N/A",
          vehicleType: getVehicleType(vehicle),
          route: vehicle.route?.name || vehicle.route?.Name || "N/A",
          driverName: driverDetails.name,
          driverPhone: driverDetails.phone,
        };
      }),
    [visibleVehicleData],
  );

  const allVehicleTableData = useMemo(() => {
    return visibleVehicleData.map((vehicle) => {
      const driverDetails = getDriverDetails(vehicle);
      const speed = getVehicleSpeed(vehicle);
      const liveDataCards = getLiveDataCards(vehicle);
      const liveDataString = liveDataCards?.length
        ? liveDataCards
            .map((card) =>
              card.typeOnly || card.display == null
                ? t(card.label)
                : `${t(card.label)}: ${card.display}`
            )
            .join(", ")
        : "--";

      return {
        id: vehicle.id,
        vehicleName: vehicle.name || vehicle.vehicleName || vehicle.VehicleName || "N/A",
        regNo: vehicle.VehicleNumber || vehicle.vehicleNumber || "N/A",
        vehicleType: getVehicleType(vehicle),
        route: vehicle.route?.name || vehicle.route?.Name || "N/A",
        driver: driverDetails.phone ? `${driverDetails.name} (${driverDetails.phone})` : driverDetails.name,
        driverName: driverDetails.name,
        driverPhone: driverDetails.phone,
        status: getVehicleStatusLabel(vehicle.status),
        originalStatus: vehicle.status,
        speedLabel: `${Math.round(speed || 0)} km/h`,
        currentLocation: getCurrentLocation(vehicle),
        lastUpdate: formatLastUpdate(getLastUpdate(vehicle)),
        lastUpdateTone: vehicle.status === "ACTIVE" ? "green" : "amber",
        liveData: liveDataString,
        liveDataCards,
        originalData: vehicle,
      };
    });
  }, [
    visibleVehicleData,
    liveTelemetryVersion,
    locationNamesByVehicleId,
    previousSensorValuesByVehicleId,
    t,
  ]);

  const vehicleTableData = useMemo(() => {
    return allVehicleTableData.slice(0, visibleVehicleCount);
  }, [allVehicleTableData, visibleVehicleCount]);

  useEffect(() => {
    setVisibleVehicleCount(VEHICLE_TABLE_BATCH_SIZE);
  }, [filters, searchQuery, activeVehicleTableTab, sortBy]);

  useEffect(() => {
    const isStillAvailable = speedThresholdOptions.some(
      (option) => option.value === speedIncidentSpeedFilter,
    );
    if (!isStillAvailable) {
      setSpeedIncidentSpeedFilter("");
    }
  }, [speedThresholdOptions, speedIncidentSpeedFilter]);

  useEffect(() => {
    setVisibleSpeedIncidentCount(SPEED_INCIDENT_TABLE_BATCH_SIZE);
  }, [
    activeVehicleTableTab,
    searchQuery,
    speedIncidentDateOption,
    speedIncidentRouteFilter,
    speedIncidentSpeedFilter,
    speedIncidentRows,
  ]);

  const visibleVehicleTableData = vehicleTableData;
  const hasMoreVehicles = visibleVehicleCount < visibleVehicleData.length;

  const handleShowMoreVehicles = () => {
    setVisibleVehicleCount((currentCount) =>
      Math.min(currentCount + VEHICLE_TABLE_BATCH_SIZE, visibleVehicleData.length),
    );
  };

  const vehicleTableFilters = [
    ...filterConfig.map((filter) => ({
      key: filter.id,
      label: `All ${filter.label || ""}`.trim(),
      value: filters[filter.id] || "",
      options: [
        { value: "", label: `All ${filter.label || "Options"}` },
        ...(filter.options || []),
      ],
      onChange: (value) => handleFilterChange({ ...filters, [filter.id]: value }),
    })),
    {
      key: "vehicleType",
      label: "All Types",
      value: filters.vehicleType || "",
      options: [{ value: "", label: "All Types" }, ...vehicleTypeOptions],
      onChange: (value) => handleFilterChange({ ...filters, vehicleType: value }),
    },
    {
      key: "sortBy",
      label: "Sort By",
      value: sortBy,
      options: [
        { value: "Newest First", label: "Newest First" },
        { value: "Oldest First", label: "Oldest First" },
      ],
      onChange: (value) => setSortBy(value),
    },
  ];

  const vehicleTableActions = canMonitorVehicle
    ? [
        {
          id: "analytics",
          label: "Analytics",
          onClick: (row) => handleVehicleAction("analytics", row.originalData),
        },
        {
          id: "monitor",
          label: "Monitor",
          icon: VisibilityOutlinedIcon,
          onClick: (row) => handleVehicleAction("monitor", row.originalData),
        },
      ]
    : [];

  const speedIncidentHeaders = [
    {
      key: "vehicle",
      label: "Vehicle",
      width: "150px",
      render: (row) => (
        <div className="vehicle-new-driver-cell">
          <strong>{row.vehicleName}</strong>
          {row.vehicleNumber ? <span>{row.vehicleNumber}</span> : null}
        </div>
      ),
    },
    { key: "route", label: "Route", width: "140px" },
    { key: "origin", label: "Origin", width: "150px" },
    { key: "destination", label: "Destination", width: "160px" },
    {
      key: "driver",
      label: "Driver",
      width: "145px",
      render: (row) => (
        <div className="vehicle-new-driver-cell">
          <strong>{row.driverName}</strong>
          {row.driverPhone ? <span>{row.driverPhone}</span> : null}
        </div>
      ),
    },
    { key: "maxSpeedLabel", label: "Max Speed (km/h)", width: "135px" },
    { key: "overspeedCount", label: "Overspeed Count", width: "135px" },
    { key: "totalDuration", label: "Total Duration", width: "135px" },
    { key: "lastOccurred", label: "Last Occurred", width: "135px" },
  ];

  const selectedRouteOption = routeOptions.find(
    (route) => String(route.value) === String(speedIncidentRouteFilter),
  );
  const speedIncidentFilteredData = speedIncidentRows.filter((row) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      [
        row.vehicleName,
        row.vehicleNumber,
        row.route,
        row.origin,
        row.destination,
        row.driverName,
        row.lastOccurred,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    const matchesRoute =
      !speedIncidentRouteFilter ||
      String(row.routeMatchValue) === String(speedIncidentRouteFilter) ||
      row.route === selectedRouteOption?.label;
    const matchesSpeed =
      !speedIncidentSpeedFilter || Number(row.maxSpeed || 0) > Number(speedIncidentSpeedFilter);

    return matchesSearch && matchesRoute && matchesSpeed;
  });
  const visibleSpeedIncidentData = speedIncidentFilteredData.slice(0, visibleSpeedIncidentCount);
  const hasMoreSpeedIncidents = visibleSpeedIncidentCount < speedIncidentFilteredData.length;
  const handleShowMoreSpeedIncidents = () => {
    setVisibleSpeedIncidentCount((currentCount) =>
      Math.min(currentCount + SPEED_INCIDENT_TABLE_BATCH_SIZE, speedIncidentFilteredData.length),
    );
  };

  const speedIncidentFilters = [
    {
      key: "date",
      label: "Today",
      value: speedIncidentDateOption,
      options: SPEED_INCIDENT_DATE_OPTIONS,
      onChange: setSpeedIncidentDateOption,
    },
    {
      key: "route",
      label: "All Routes",
      value: speedIncidentRouteFilter,
      options: [{ value: "", label: "All Routes" }, ...routeOptions],
      onChange: setSpeedIncidentRouteFilter,
    },
    {
      key: "speed",
      label: "All Speeds",
      value: speedIncidentSpeedFilter,
      options: speedThresholdOptions,
      onChange: setSpeedIncidentSpeedFilter,
    },
  ];
  const isSpeedIncidentsTab = activeVehicleTableTab === "speedIncidents";
  const currentTableHeaders = isSpeedIncidentsTab ? speedIncidentHeaders : vehicleTableHeaders;
  const currentTableData = isSpeedIncidentsTab ? visibleSpeedIncidentData : visibleVehicleTableData;
  const allCurrentTableData = isSpeedIncidentsTab ? speedIncidentFilteredData : allVehicleTableData;
  const currentTableFilters = isSpeedIncidentsTab ? speedIncidentFilters : vehicleTableFilters;
  const currentTableActions = isSpeedIncidentsTab ? [] : vehicleTableActions;
  const currentTableLoading = isSpeedIncidentsTab ? isSpeedIncidentLoading : isLoading;

  const userState = localStorage.getItem("userState");
  const parsedUserState = userState ? JSON.parse(userState) : null;
  const cleanOrgName = (
    parsedUserState?.organization?.name ||
    orgName ||
    "RouteEye"
  ).replace(/[^a-zA-Z0-9_-]/g, "_");

  const handleDownloadVehicles = async () => {
    if (isSpeedIncidentsTab) return;
    const resolvedOrgId = parsedUserState?.user?.organizationId || getOrganizationId() || filters.organization;
    const downloadFileName = `${cleanOrgName}_Vehicles.xlsx`;

    const downloadParams = {
      organizationId: resolvedOrgId,
      table: "vehicle",
      format: "xlsx",
      includeDetails: true,
      search: searchQuery || undefined,
    };

    if (selectedVehicleIds && selectedVehicleIds.length > 0) {
      downloadParams.ids = selectedVehicleIds.join(",");
      downloadParams.vehicleIds = selectedVehicleIds.join(",");
    }

    try {
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        method: "GET",
        params: downloadParams,
        defaultFileName: downloadFileName,
      });
    } catch (err) {
      console.warn("Backend /excel/download table=vehicle failed, trying /excel/download/vehicle:", err);
      try {
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download/vehicle`,
          params: downloadParams,
          defaultFileName: downloadFileName,
        });
      } catch (fallbackErr) {
        console.warn("Backend export not available, using client CSV fallback:", fallbackErr);
        exportTableToCSV(`${cleanOrgName}_Vehicles`, vehicleTableHeaders, allVehicleTableData);
      }
    }
  };

  const handleDownloadSpeedIncidents = async () => {
    const resolvedOrgId = parsedUserState?.user?.organizationId || getOrganizationId() || filters.organization;
    const downloadFileName = `${cleanOrgName}_Speed_Incidents.xlsx`;

    const targetRows = (selectedSpeedIncidentKeys && selectedSpeedIncidentKeys.length > 0)
      ? speedIncidentFilteredData.filter((row) =>
          selectedSpeedIncidentKeys.some((id) => String(id) === String(row.id))
        )
      : speedIncidentFilteredData;

    const exportRows = targetRows.map((row) => ({
      "Vehicle": row.vehicleName || row.vehicle || "-",
      "Vehicle Number": row.vehicleNumber || "-",
      "Route": row.route || "-",
      "Origin": row.origin || "-",
      "Destination": row.destination || "-",
      "Driver": row.driverName || "-",
      "Driver Phone": row.driverPhone || "-",
      "Max Speed (km/h)": row.maxSpeed != null ? String(row.maxSpeed) : (row.maxSpeedLabel || "-"),
      "Overspeed Count": row.overspeedCount != null ? row.overspeedCount : "-",
      "Total Duration": row.totalDuration || "-",
      "Last Occurred": row.lastOccurred || "-",
    }));

    try {
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        method: "POST",
        data: {
          table: "speed_incidents",
          organizationId: resolvedOrgId,
          format: "xlsx",
          includeDetails: true,
          rows: exportRows,
        },
        defaultFileName: downloadFileName,
      });
    } catch (err) {
      console.warn("Backend POST /excel/download speed_incidents failed, trying table=overspeed_incidents:", err);
      try {
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download`,
          method: "POST",
          data: {
            table: "overspeed_incidents",
            organizationId: resolvedOrgId,
            format: "xlsx",
            includeDetails: true,
            rows: exportRows,
          },
          defaultFileName: downloadFileName,
        });
      } catch (err2) {
        console.warn("Backend Excel export for speed incidents failed, using client CSV fallback:", err2);
        const detailedHeaders = [
          { key: "Vehicle", label: "Vehicle" },
          { key: "Vehicle Number", label: "Vehicle Number" },
          { key: "Route", label: "Route" },
          { key: "Origin", label: "Origin" },
          { key: "Destination", label: "Destination" },
          { key: "Driver", label: "Driver" },
          { key: "Driver Phone", label: "Driver Phone" },
          { key: "Max Speed (km/h)", label: "Max Speed (km/h)" },
          { key: "Overspeed Count", label: "Overspeed Count" },
          { key: "Total Duration", label: "Total Duration" },
          { key: "Last Occurred", label: "Last Occurred" },
        ];
        exportTableToCSV(`${cleanOrgName}_Speed_Incidents`, detailedHeaders, exportRows);
      }
    }
  };

  return (
    <>
      {/* <SubTitle
        title={pageTitle}
        path={`${orgName || "organization"} > Vehicles`}
        add={canAddVehicle ? createAction?.labelKey || "Create Vehicles" : ""}
        addPath={canAddVehicle ? "/vehicle/add" : ""}
        track={monitorAction?.labelKey || "monitorVehicles"}
        trackPath={"/vehicle/TrackAllVehicle"}
        track2={entryExitAction?.labelKey || "Vehicles Entry/Exit Register"}
        onAddClick={canAddVehicle ? handleAddVehicle : null}
        onTrackClick={handleTrackAllVehicles}
        onTrack2Click={handleEntryExitRegister}
      /> */}
      <SubTitleNew
        title={pageTitle}
        path={`${orgName || "organization"} > Vehicles`}
        add={canAddVehicle ? createAction?.labelKey || "Create Vehicles" : undefined}
        onAddClick={canAddVehicle ? handleAddVehicle : undefined}
        track={organizationInactive ? undefined : monitorAction?.labelKey || "Monitor Vehicles"}
        trackPath={organizationInactive ? undefined : "/vehicle/TrackAllVehicle"}
        track2={organizationInactive ? undefined : entryExitAction?.labelKey || "Vehicle Entry/Exit Register"}
        onTrackClick={organizationInactive ? undefined : handleTrackAllVehicles}
        onTrack2Click={organizationInactive ? undefined : handleEntryExitRegister}
        showDashboardControls={!organizationInactive}
        dateOptions={organizationInactive ? undefined : DASHBOARD_DATE_OPTIONS}
        dateError={dashboardError}
        onDateRangeChange={organizationInactive ? undefined : handleDashboardDateRangeChange}
      />
      <div className="vehicle-new-page-body">
        {canAddVehicle && showCreateVehicle && (
          <div className="vehicle-new-create-panel">
            <VehicleAddMain
              embedded
              onCancel={() => setShowCreateVehicle(false)}
              onComplete={handleCreateVehicleComplete}
            />
          </div>
        )}
        {organizationInactive ? (
          <InactiveInventoryTable
            title="vehicleInventory"
            headers={vehicleInventoryHeaders}
            data={vehicleInventoryData}
            loading={isLoading}
            emptyMessage={t("No vehicles found")}
            searchPlaceholder={t("Search vehicles")}
            itemLabel="vehicles"
            minWidth={880}
          />
        ) : (
          <>
            {dashboardError && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error" onClose={() => setDashboardError("")}>
                  {t(dashboardError)}
                </Alert>
              </Box>
            )}
            <div className="stats-card-grid vehicle-new-stats-grid">
              {vehicleStatsCards.map((card, index) => (
                <StatsCard
                  key={`${card.title}-${index}`}
                  title={card.title}
                  value={card.value}
                  icon={card.icon || vehicleStatsCards[index % vehicleStatsCards.length]?.icon}
                  tone={card.tone || vehicleStatsCards[index % vehicleStatsCards.length]?.tone}
                  trend={card.trend}
                  trendDirection={card.trendDirection}
                  loading={fleetMetricLoading[card.loadingSource]}
                />
              ))}
            </div>
            <DashboardGraph
              vehicleData={filteredVehicleData}
              monitorDevicesByVehicleId={monitorDevicesByVehicleId}
              liveLocationsByVehicleId={liveLocationsByVehicleId}
              overspeedLimit={settings?.maxSpeed}
              overspeedIncidentCount={dashboardOverspeedCount}
              organizationId={filters.organization}
              siteId={filters.site}
              dateOption={dashboardDateOption}
              customStartDate={dashboardCustomRange.start}
              customEndDate={dashboardCustomRange.end}
              error={dashboardError}
            />
            <div className="table_container vehicle-new-table-container">
              <DynamicTable
                title={isSpeedIncidentsTab ? "Speed Incidents" : "All Vehicles"}
                className="vehicle-new-fleet-table"
                headers={currentTableHeaders}
                data={currentTableData}
                downloadData={
                  isSpeedIncidentsTab
                    ? ((selectedSpeedIncidentKeys && selectedSpeedIncidentKeys.length > 0)
                        ? speedIncidentFilteredData.filter((row) =>
                            selectedSpeedIncidentKeys.some((id) => String(id) === String(row.id))
                          )
                        : speedIncidentFilteredData)
                    : allVehicleTableData
                }
                downloadFileName={
                  isSpeedIncidentsTab
                    ? `${cleanOrgName}_Speed_Incidents`
                    : `${cleanOrgName}_Vehicles`
                }
                selectable={true}
                selectedRowKeys={isSpeedIncidentsTab ? selectedSpeedIncidentKeys : selectedVehicleIds}
                onSelectionChange={isSpeedIncidentsTab ? setSelectedSpeedIncidentKeys : setSelectedVehicleIds}
                onDownload={isSpeedIncidentsTab ? handleDownloadSpeedIncidents : handleDownloadVehicles}
                filters={currentTableFilters}
                tabs={[
                  { label: "Overview", value: "overview" },
                  { label: "Speed Incidents", value: "speedIncidents" },
                ]}
                activeTab={activeVehicleTableTab}
                onTabChange={handleVehicleTableTabChange}
                searchValue={searchQuery}
                onSearchChange={handleSearchChange}
                searchPlaceholder={isSpeedIncidentsTab ? "Search incidents" : "Search vehicles"}
                actions={currentTableActions}
                loading={currentTableLoading}
                emptyMessage={isSpeedIncidentsTab ? "No overspeed incidents found" : "No vehicles found"}
                headerVariant="light"
                minWidth={isSpeedIncidentsTab ? 1280 : 1180}
                rowKey={(row) => row.id}
                onRowClick={
                  canUpdateVehicle && !isSpeedIncidentsTab
                    ? (row) => toggleExpandedVehicleRow(row.id)
                    : undefined
                }
                renderExpandedRow={
                  canUpdateVehicle && !isSpeedIncidentsTab
                    ? (row) => {
                    if (String(expandedVehicleId || "") !== String(row.id || "")) {
                      return null;
                    }

                    return (
                      <div className="vehicle-new-expanded-form">
                        <VehicleAddMain
                          embedded
                          embeddedVehicleId={row.id}
                          onCancel={() => setExpandedVehicleId(null)}
                          onComplete={handleUpdateVehicleComplete}
                          onDelete={
                            canDeleteVehicle
                              ? () => handleVehicleAction("delete", row.originalData)
                              : undefined
                          }
                        />
                      </div>
                    );
                  }
                    : undefined
                }
                pagination={{
                  enabled: false,
                  pageSize: isSpeedIncidentsTab
                    ? SPEED_INCIDENT_TABLE_BATCH_SIZE
                    : VEHICLE_TABLE_BATCH_SIZE,
                  itemLabel: isSpeedIncidentsTab ? "incidents" : "vehicles",
                }}
              />
              {isSpeedIncidentsTab ? (
                <div className="vehicle-show-more-footer">
                  <span>
                    {t("Showing")} {visibleSpeedIncidentData.length} {t("of")}{" "}
                    {speedIncidentFilteredData.length} {t("incidents")}
                  </span>
                  {hasMoreSpeedIncidents && (
                    <button type="button" onClick={handleShowMoreSpeedIncidents}>
                      {t("Show more")}
                    </button>
                  )}
                </div>
              ) : hasMoreVehicles ? (
                <div className="vehicle-show-more-footer">
                  <span>
                    {t("Showing")} {visibleVehicleTableData.length} {t("of")}{" "}
                    {visibleVehicleData.length} {t("vehicles")}
                  </span>
                  <button type="button" onClick={handleShowMoreVehicles}>
                    {t("Show more")}
                  </button>
                </div>
              ) : null}
            </div>
          </>
        )}


      {/* <Cards cardData={layoutStatCards || legacyStatCards} /> */}

      {/* <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense fallback={<div className="loader"></div>}>
          <DynamicTable
            headers={filteredHeaders}
            data={formatDataForTable(
              isRouteEyeAdmin()
                ? filteredVehicleData || []
                : filteredVehicleData || []
            )}
            onActionClick={handleActionClick}
            actions={tableActions}
            loading={isLoading}
            key={JSON.stringify(filters)}
          />
        </Suspense>
      </div> */}
      {/* <div style={{ height: "20vh" }}></div> */}
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Vehicle!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {vehicleToDelete?.name || t("this vehicle")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </>
  );
}
